import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
  createStyles,
} from '@material-ui/core';
import { useForm, FormContext } from 'react-hook-form';
import { lightFormat } from 'date-fns';
import { ErrorDisplay, useFormEvents, useObservable } from '@lib/common';
import { CorporateAccount, FormStatus, Reservation } from '@lib/state';
import { useCorporateAccountDelegates } from 'app/corporate-account/useCorporateAccountDelegates';
import {
  BulkChangeBillingForms,
  getBulkChangeBillingQuery,
  getBulkChangeBillingService,
} from 'app/state/bulk-change-billing';
import { PreviewFilter, isRowBlocked } from '../bulk-change-billing-utils';
import { ConfigureStep, DELEGATE_PLACEHOLDER } from './bulk-change-billing-configure-step';
import { PreviewSummary } from './bulk-change-billing-preview-summary';
import { PreviewTable } from './bulk-change-billing-preview-table';

type Step = 'configure' | 'preview';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles(theme =>
  createStyles({
    dialogTitle: {
      padding: theme.spacing(1.5, 3),
    },
    dialogSubtitle: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(0, 1),
      padding: theme.spacing(0, 3, 1),
      lineHeight: 1.5,
    },
  })
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ConfigureFormValues {
  newCorporateAccountId: string;
  newDelegateUserId: string;
  preserveExistingDelegate: boolean;
}

interface Props {
  open: boolean;
  propertyId: string;
  reservationIds: string[];
  selectedReservations: Reservation[];
  corporateAccounts: CorporateAccount[];
  /** Effective start date used to scope the preview's financial impact window. */
  effectiveStartDate?: Date;
  /** Effective end date used to scope the preview's financial impact window. */
  effectiveEndDate?: Date;
  onClose: () => void;
  onSuccess: (runId: string) => void;
}

export const BulkChangeBillingInitiateDialog: React.FC<Props> = ({
  open,
  propertyId,
  reservationIds,
  selectedReservations,
  corporateAccounts,
  effectiveStartDate,
  effectiveEndDate,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('configure');
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  // Eligible reservation IDs the user has selected on the preview step. Owned here
  // so summary tiles, the table, the subtitle, and the confirm button stay in sync.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const styles = useStyles();

  // Detect if all selected reservations share the same corporate account
  const sameCorporateAccountId = useMemo(() => {
    const ids = new Set(
      selectedReservations.map(r => r.affiliation?.corporateAccountId).filter(Boolean)
    );
    return ids.size === 1 ? [...ids][0]! : undefined;
  }, [selectedReservations]);

  const [{ status: previewStatus, error: previewError }, resetPreviewUI] = useFormEvents(
    BulkChangeBillingForms.Preview
  );
  const [{ status: createStatus, error: createError }, resetCreateUI] = useFormEvents(
    BulkChangeBillingForms.Create
  );

  const preview = useObservable(getBulkChangeBillingQuery().preview);
  const pendingRunId = useObservable(getBulkChangeBillingQuery().pendingRunId);

  const form = useForm<ConfigureFormValues>({
    defaultValues: {
      newCorporateAccountId: sameCorporateAccountId ?? '',
      newDelegateUserId: DELEGATE_PLACEHOLDER,
      preserveExistingDelegate: false,
    },
  });

  // Reset form when sameCorporateAccountId changes (e.g. dialog reopens with different selection)
  // Note: form.reset() is used instead of form.setValue() because setValue does not trigger
  // a re-render in react-hook-form v5, so watch() would not reflect the updated value.
  useEffect(() => {
    form.reset({
      newCorporateAccountId: sameCorporateAccountId ?? '',
      newDelegateUserId: DELEGATE_PLACEHOLDER,
      preserveExistingDelegate: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameCorporateAccountId]);

  const {
    newCorporateAccountId: watchedAccountId,
    newDelegateUserId: watchedDelegateId,
    preserveExistingDelegate: watchedPreserveDelegate,
  } = form.watch({ nest: true });

  const delegates = useCorporateAccountDelegates(watchedAccountId || undefined);

  // Delegate is only optional when all reservations already belong to the selected account
  // AND the user has not opted to preserve existing delegates. When `preserveExistingDelegate`
  // is checked, a delegate selection is required as the fallback used whenever a reservation's
  // existing delegate isn't valid for the new corporate account.
  const isSameAccount = !!sameCorporateAccountId && watchedAccountId === sameCorporateAccountId;
  const isDelegateOptional = isSameAccount && !watchedPreserveDelegate;

  useEffect(() => {
    if (previewStatus === FormStatus.Success) {
      setStep('preview');
    }
  }, [previewStatus]);

  // Seed selection with all eligible reservation IDs whenever the user enters the
  // preview step with a fresh preview. Going Back → re-Preview always resets
  // selections to the new defaults.
  useEffect(() => {
    if (step !== 'preview' || !preview?.data) return;
    const next = new Set<string>();
    for (const row of preview.data) {
      if (row.reservationId && !isRowBlocked(row)) {
        next.add(row.reservationId);
      }
    }
    setSelectedIds(next);
  }, [step, preview]);

  useEffect(() => {
    if (createStatus === FormStatus.Success && pendingRunId) {
      onSuccess(pendingRunId);
    }
  }, [createStatus, pendingRunId, onSuccess]);

  const handleClose = () => {
    form.reset();
    setStep('configure');
    setPreviewFilter('all');
    setSelectedIds(new Set());
    resetPreviewUI();
    resetCreateUI();
    onClose();
  };

  const handlePreview = form.handleSubmit(values => {
    if (!values.newCorporateAccountId) return;
    if (!effectiveStartDate || !effectiveEndDate) return;
    getBulkChangeBillingService().previewRun({
      propertyId,
      reservationIds,
      effectiveStartDate: lightFormat(effectiveStartDate, 'yyyy-MM-dd'),
      effectiveEndDate: lightFormat(effectiveEndDate, 'yyyy-MM-dd'),
      newCorporateAccountId: values.newCorporateAccountId,
      newDelegateUserId:
        values.newDelegateUserId === DELEGATE_PLACEHOLDER ? undefined : values.newDelegateUserId,
      preserveExistingDelegate: !!values.preserveExistingDelegate,
    });
  });

  const handleConfirm = () => {
    if (!preview?.corporateAccountId) return;
    getBulkChangeBillingService().createRun({
      propertyId,
      // Only the user-selected subset is sent for execution. Blocked rows are
      // excluded by the preview-table's isRowSelectable guard.
      reservationIds: Array.from(selectedIds),
      newCorporateAccountId: preview.corporateAccountId,
      newDelegateUserId: preview.newDelegateUserId ?? undefined,
      preserveExistingDelegate: !!watchedPreserveDelegate,
    });
  };

  const isPreviewStep = step === 'preview';
  const isPreviewLoading = previewStatus === FormStatus.Pending;
  const isCreateLoading = createStatus === FormStatus.Pending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={isPreviewStep ? 'xl' : 'sm'} fullWidth>
      <DialogTitle className={styles.dialogTitle}>
        {isPreviewStep ? 'Review Bulk Change Billing' : 'Initiate Bulk Change Billing'}
      </DialogTitle>
      {isPreviewStep && preview && (
        <Typography
          variant="body2"
          color="textSecondary"
          component="div"
          className={styles.dialogSubtitle}
        >
          <span>
            New corporate account:{' '}
            <strong>{preview.corporateAccountName ?? preview.corporateAccountId}</strong>
          </span>
          <span>
            · Delegate:{' '}
            <strong>
              {preview.newDelegateUserId
                ? preview.newDelegateName || '—'
                : 'Keep existing delegate'}
            </strong>
          </span>
          <span>
            · <strong>{selectedIds.size}</strong> will be updated
            {(preview.totalIneligibleReservations ?? 0) > 0 && (
              <>
                {' · '}
                <strong>{preview.totalIneligibleReservations}</strong> blocked
              </>
            )}
          </span>
        </Typography>
      )}
      <FormContext {...form}>
        <DialogContent>
          {!isPreviewStep && (
            <ConfigureStep
              reservationCount={reservationIds.length}
              corporateAccounts={corporateAccounts}
              delegates={delegates}
              register={form.register}
              watchedAccountId={watchedAccountId}
              isSameAccount={isSameAccount}
              preserveExistingDelegate={!!watchedPreserveDelegate}
            />
          )}

          {isPreviewStep && preview && (
            <>
              <PreviewSummary
                preview={preview}
                rows={preview.data ?? []}
                activeFilter={previewFilter}
                onFilterChange={setPreviewFilter}
                selectedIds={selectedIds}
              />
              <PreviewTable
                rows={preview.data ?? []}
                filter={previewFilter}
                effectiveStartDate={effectiveStartDate}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              />
            </>
          )}

          {(previewError || createError) && <ErrorDisplay error={previewError ?? createError} />}
        </DialogContent>

        <DialogActions>
          {!isPreviewStep ? (
            <>
              <Button onClick={handleClose} disabled={isPreviewLoading}>
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                color="primary"
                variant="contained"
                disabled={
                  isPreviewLoading ||
                  !watchedAccountId ||
                  !effectiveStartDate ||
                  !effectiveEndDate ||
                  (!isDelegateOptional &&
                    (!watchedDelegateId || watchedDelegateId === DELEGATE_PLACEHOLDER))
                }
              >
                {isPreviewLoading ? 'Loading Preview…' : 'Preview'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setStep('configure')} disabled={isCreateLoading}>
                Back
              </Button>
              <Button onClick={handleClose} disabled={isCreateLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirm}
                color="primary"
                variant="contained"
                disabled={isCreateLoading || selectedIds.size === 0}
              >
                {isCreateLoading
                  ? 'Initiating…'
                  : `Confirm & Initiate (${selectedIds.size} update${
                      selectedIds.size !== 1 ? 's' : ''
                    })`}
              </Button>
            </>
          )}
        </DialogActions>
      </FormContext>
    </Dialog>
  );
};
